import * as React from 'react';
import { set } from 'lodash-es';
import { IVector2 } from '@easytwin/runtime';
import { toScaledVec2 } from '@/utils';
import { ProInputNumber, ProInputNumberProps } from './ProInputNumber';
import { ImmerFormItem } from './ImmerFormItem';
import { FormItemsInRow } from './FormItemsInRow';
import { LockIcon } from './LockIcon';

interface Vector2FormItemProps
  extends Omit<
    ProInputNumberProps,
    'onChange' | 'value' | 'onBlur' | 'prefix' | 'suffix' | 'onPointMoveStart' | 'onPointMoveEnd'
  > {
  label: string;
  value: IVector2;
  constrainable?: boolean;
  prefix?: [string, string];
  suffix?: [string, string];
  onChange?: (name: string, value: IVector2 | undefined) => void;
  onBlur: (name: string, value: IVector2 | undefined) => void;
  tooltip?: React.ReactNode;
}

export const Vector2FormItem: React.FC<Vector2FormItemProps> = (props) => {
  const {
    label,
    value,
    constrainable,
    prefix,
    suffix,
    name,
    onBlur,
    onChange,
    tooltip,
    disabled,
    ...rest
  } = props;

  const [isScaled, setIsScaled] = React.useState(false);
  const originVector2Ref = React.useRef<IVector2 | undefined>(undefined);

  const handleChange = (n: string, v: number) => {
    const value = originVector2Ref.current;
    if (!value) return;
    const newValue = { ...value };
    set(newValue, n, v);
    const scaledValue = isScaled ? toScaledVec2(newValue, value) : newValue;
    onChange?.(name, scaledValue);
  };

  const handleBlur = (n: string, v: number) => {
    const value = originVector2Ref.current;
    if (!value) return;
    const newValue = { ...value };
    set(newValue, n, v);
    const scaledValue = isScaled ? toScaledVec2(newValue, value) : newValue;
    onBlur(name, scaledValue);
  };

  const handleStartInput = () => {
    originVector2Ref.current = value;
  };

  const handleEndInput = (n: string, v: number) => {
    handleBlur(n, v);
    originVector2Ref.current = undefined;
  };

  return (
    <ImmerFormItem
      disabled={disabled}
      label={label}
      nodeAfter={constrainable && <LockIcon value={isScaled} onChange={setIsScaled} size={14} />}
      tooltip={tooltip}
    >
      <FormItemsInRow>
        <ProInputNumber
          name='x'
          value={value.x}
          prefix={prefix?.[0]}
          suffix={suffix?.[0]}
          disabled={disabled}
          {...rest}
          onPointMoveStart={handleStartInput}
          onPointMoveEnd={handleEndInput}
          onFocus={handleStartInput}
          onChange={handleChange}
          onBlur={handleEndInput}
        />
        <ProInputNumber
          name='y'
          value={value.y}
          prefix={prefix?.[1]}
          suffix={suffix?.[1]}
          disabled={disabled}
          {...rest}
          onPointMoveStart={handleStartInput}
          onPointMoveEnd={handleEndInput}
          onFocus={handleStartInput}
          onChange={handleChange}
          onBlur={handleEndInput}
        />
      </FormItemsInRow>
    </ImmerFormItem>
  );
};
